import introJs from "intro.js";
import { buildWorkbookSteps } from "./labAreaTour/workbookTourSteps.js";
import { getLabTourStepBuilders } from "@/labRegistry.js";

const LAB_OFFCANVAS_ID = "labAreaOffcanvas";

/** Pre-namespaced key: one global "seen tour" for all labs. */
const LEGACY_TOUR_VIEWED_KEY = "lab-area-tour-viewed";

function tourViewedStorageKey(labId) {
  return `lab-area-tour-viewed:${labId}`;
}

/** Tab steps: highlight is often wrong on Next (scroll/layout/Chart.js) until a deferred refresh. */
const TAB_DATA_TOUR_IDS = new Set(["chart-tab", "table-tab", "log-tab"]);

/**
 * Reposition the highlight after scroll/paint (fixes wrong box on Next for tab steps).
 * Always uses refresh(false): refresh(true) re-fetches steps and causes visible flicker on desktop and mobile.
 * @param {import("intro.js").IntroJs} intro
 * @param {string | null} tourId - data-tour value of current step target
 */
function scheduleIntroHighlightRefresh(intro, tourId) {
  const refresh = () => intro.refresh(false);
  requestAnimationFrame(() => {
    requestAnimationFrame(refresh);
  });
  if (tourId === "chart-tab") {
    setTimeout(refresh, 150);
  }
}

/**
 * Returns whether the user has viewed the Lab Area tour for this lab at least once.
 * Honors the legacy storage key (pre–per-lab keys) so existing users are not nagged.
 * @param {string} labId
 * @returns {boolean}
 */
export function hasViewedLabAreaTour(labId) {
  try {
    if (localStorage.getItem(LEGACY_TOUR_VIEWED_KEY) === "true") return true;
    if (!labId) return false;
    return localStorage.getItem(tourViewedStorageKey(labId)) === "true";
  } catch {
    return false;
  }
}

/**
 * Resets the Lab Area tour state for one lab (or the legacy global key when `labId` is omitted).
 * @param {string} [labId]
 */
export function resetLabAreaTourState(labId) {
  try {
    if (labId) {
      localStorage.removeItem(tourViewedStorageKey(labId));
    } else {
      localStorage.removeItem(LEGACY_TOUR_VIEWED_KEY);
    }
  } catch {
    // localStorage may be unavailable
  }
}

/**
 * @param {string} labId
 */
function markTourViewed(labId) {
  try {
    localStorage.setItem(tourViewedStorageKey(labId), "true");
  } catch {
    // localStorage may be unavailable (private browsing, etc.)
  }
}

/**
 * Builds and starts an Intro.js tour for the Workbook and Lab Area.
 * Tour steps are conditional based on mobile vs desktop layout and lab `labId`.
 *
 * @param {Object} options
 * @param {string} options.labId — selects lab-specific Lab Area steps from the tour registry
 * @param {boolean} options.inMobileView - Whether the mobile layout is active
 * @param {boolean} options.showSimpleUI - Effective Simple UI (Vuex getter: user preference or forced on when inMobileView)
 * @param {Object} [options.labAreaOffcanvasRef] - Vue component ref with show() method (mobile only)
 * @param {Function} [options.onTourStart] - Called once when the tour actually starts (after steps are built)
 * @param {Function} [options.onTourEnd] - Called once when the tour completes or is dismissed
 * @returns {Function} startTour - Call to begin the tour
 */
export function useLabAreaTour({
  labId,
  inMobileView,
  showSimpleUI,
  labAreaOffcanvasRef,
  onTourStart,
  onTourEnd
}) {
  function startTour() {
    const { buildDesktopLabSteps, buildMobileLabSteps } =
      getLabTourStepBuilders(labId);
    const steps = inMobileView
      ? [...buildWorkbookSteps(), ...buildMobileLabSteps()]
      : [...buildWorkbookSteps(), ...buildDesktopLabSteps(showSimpleUI)];

    if (steps.length === 0) return;

    onTourStart?.();

    const intro = introJs();

    intro.setOptions({
      exitOnEsc: true,
      exitOnOverlayClick: true,
      showStepNumbers: true,
      scrollToElement: true
    });

    intro.addSteps(steps);

    let finishTourRan = false;
    function finishTour() {
      if (finishTourRan) return;
      finishTourRan = true;
      onTourEnd?.();
      markTourViewed(labId);
      if (inMobileView && labAreaOffcanvasRef?.hide) {
        labAreaOffcanvasRef.hide();
      }
    }

    intro.onComplete(finishTour);
    intro.onExit(finishTour);

    intro.onAfterChange((targetElement) => {
      const tourId = targetElement?.getAttribute?.("data-tour") ?? null;
      if (!tourId || !TAB_DATA_TOUR_IDS.has(tourId)) return;
      scheduleIntroHighlightRefresh(intro, tourId);
    });

    if (inMobileView && labAreaOffcanvasRef?.show) {
      const offcanvasRoot = document.getElementById(LAB_OFFCANVAS_ID);
      intro.onBeforeChange((targetElement) => {
        if (!targetElement || !offcanvasRoot) return;
        if (!offcanvasRoot.contains(targetElement)) return;
        if (offcanvasRoot.classList.contains("show")) return;
        offcanvasRoot.addEventListener(
          "shown.bs.offcanvas",
          () => {
            intro.refresh(false);
          },
          { once: true }
        );
        labAreaOffcanvasRef.show();
      });
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        intro.start();
      });
    });
  }

  return { startTour };
}
